import { ProjectService } from 'config/services/project.service';
import { useCachedResource } from './useCachedResource';

type ParamValue = string | number | boolean | null | undefined | (string | number)[] | Record<string, unknown>;
export type TotalsParams = Record<string, ParamValue>;

/** Totals of the Acciones matching some filters: count and estimated investment in USD. */
export interface ActionTotals {
  totalCount: number;
  /** `null` when the totals could not be loaded. */
  totalInvestment: number | null;
}

/** The `index_extra_data` query string for the set parameters (lists comma-joined, objects as key[sub]). */
export const totalsQuery = (params: TotalsParams): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') {
      return;
    }
    if (Array.isArray(value)) {
      query.append(key, value.join(','));
    } else if (typeof value === 'object') {
      Object.entries(value).forEach(([sub, subValue]) => query.append(`${key}[${sub}]`, String(subValue)));
    } else {
      query.append(key, String(value));
    }
  });
  const text = query.toString();
  return text ? `?${text}` : '';
};

/** The react-admin list filter kept in the URL (`?filter=<json>`), as totals parameters. */
export const paramsFromListSearch = (search: string): TotalsParams => {
  const filter = new URLSearchParams(search).get('filter');
  if (!filter) {
    return {};
  }
  try {
    return JSON.parse(filter);
  } catch {
    return {};
  }
};

const cache = new Map<string, ActionTotals>();
const service = new ProjectService();
const NO_TOTALS: ActionTotals = { totalCount: 0, totalInvestment: null };

/** The Acciones totals for `params`, or `null` while they load. */
export const useActionTotals = (params: TotalsParams): ActionTotals | null => {
  const query = totalsQuery(params);
  return useCachedResource(cache, query, () => service.get_projects_list_extra_data(query), NO_TOTALS);
};
